import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Download, Search as SearchIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdvertiserReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [advertisers, setAdvertisers] = useState([]);
  const [sortColumn, setSortColumn] = useState('advertiser_id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchCriteria, setSearchCriteria] = useState({
    magazine: '',
    advertiser_id: '',
    name: '',
    company_name: '',
    email: '',
    status: ''
  });

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/advertisers/search`, searchCriteria);
      setAdvertisers(response.data);
      
      if (response.data.length === 0) {
        toast.info('No advertisers found matching the criteria');
      } else {
        toast.success(`Found ${response.data.length} advertiser(s)`);
      }
    } catch (error) {
      console.error('Error searching advertisers:', error);
      toast.error('Failed to search advertisers');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedAdvertisers = () => {
    if (!sortColumn) return advertisers;

    return [...advertisers].sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';

      // Special handling for advertiser_id - extract numeric part
      if (sortColumn === 'advertiser_id') {
        const extractNumber = (id) => {
          const match = id.match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        aVal = extractNumber(aVal);
        bVal = extractNumber(bVal);
      }
      // Handle date columns
      else if (sortColumn === 'created_at' || sortColumn === 'updated_at' || sortColumn === 'end_date') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      // Handle numeric columns
      else if (sortColumn === 'amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      // Handle string columns
      else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleReset = () => {
    setSearchCriteria({
      magazine: '',
      advertiser_id: '',
      name: '',
      company_name: '',
      email: '',
      status: ''
    });
    setAdvertisers([]);
    setSortColumn('');
    setSortDirection('asc');
  };

  const handlePrint = () => {
    // Set document title for PDF filename
    document.title = 'Advertiser Report';
    window.print();
    // Reset title after print
    setTimeout(() => { document.title = 'Magazine CRM'; }, 1000);
  };

  const exportToCSV = () => {
    if (advertisers.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Get sorted advertisers to export in the same order as displayed
      const sortedAdvertisers = getSortedAdvertisers();
      
      const headers = ['ID', 'Name', 'Company', 'District', 'State', 'Pincode', 'Status', 'Amount', 'Payment Status', 'End Date', 'Contact No', 'WhatsApp No', 'Email', 'Created Date', 'Last Modified'];
      const csvContent = [
        headers.join(','),
        ...sortedAdvertisers.map(adv => [
          adv.advertiser_id || '',
          `"${adv.name || ''}"`,
          `"${adv.company_name || ''}"`,
          `"${adv.district || ''}"`,
          adv.state || '',
          adv.pincode || '',
          adv.status || '',
          adv.amount || '',
          adv.payment_status || '',
          `${adv.end_month || ''} ${adv.end_year || ''}`,
          adv.contact_no || '',
          adv.whatsapp_no || '',
          adv.email || '',
          adv.created_at ? new Date(adv.created_at).toLocaleString('en-IN') : '',
          adv.updated_at ? new Date(adv.updated_at).toLocaleString('en-IN') : ''
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `Advertiser_Report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      toast.success('Report downloaded! Check your Downloads folder');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8 no-print">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#059669' }} data-testid="reports-title">
              Advertiser Reports
            </h1>
          </div>
          {advertisers.length > 0 && (
            <div className="flex space-x-2">
              <Button onClick={exportToCSV} className="btn-secondary" data-testid="export-button">
                <Download className="mr-2" size={18} />
                Export to Excel
              </Button>
              <Button onClick={handlePrint} className="btn-primary" style={{ backgroundColor: '#059669' }} data-testid="print-button">
                <FileText className="mr-2" size={18} />
                Print Report
              </Button>
            </div>
          )}
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="form-section mb-8 no-print">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#059669' }}>Search Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Magazine</Label>
              <Select value={searchCriteria.magazine} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, magazine: value })}>
                <SelectTrigger data-testid="magazine-filter">
                  <SelectValue placeholder="Select magazine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={searchCriteria.status} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, status: value })}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Advertiser ID</Label>
              <Input data-testid="advertiser-id-input" value={searchCriteria.advertiser_id} onChange={(e) => setSearchCriteria({ ...searchCriteria, advertiser_id: e.target.value })} placeholder="Enter advertiser ID" />
            </div>

            <div className="space-y-2">
              <Label>Name</Label>
              <Input data-testid="name-input" value={searchCriteria.name} onChange={(e) => setSearchCriteria({ ...searchCriteria, name: e.target.value })} placeholder="Enter name" />
            </div>

            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input data-testid="company-name-input" value={searchCriteria.company_name} onChange={(e) => setSearchCriteria({ ...searchCriteria, company_name: e.target.value })} placeholder="Enter company name" />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="email-input" value={searchCriteria.email} onChange={(e) => setSearchCriteria({ ...searchCriteria, email: e.target.value })} placeholder="Enter email" />
            </div>
          </div>

          <div className="mt-8 flex space-x-4">
            <Button type="submit" disabled={loading} className="btn-primary" style={{ backgroundColor: '#059669' }} data-testid="search-button">
              <SearchIcon className="mr-2" size={18} />
              {loading ? 'Searching...' : 'Search'}
            </Button>
            <Button type="button" onClick={handleReset} variant="outline" data-testid="reset-button">
              Reset
            </Button>
          </div>
        </form>

        {/* Report Table */}
        {loading ? (
          <div className="loader" />
        ) : advertisers.length > 0 ? (
          <div className="form-section">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#059669' }}>
              Total Advertisers: {advertisers.length}
            </h2>
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <table className="data-table" data-testid="reports-table" style={{ minWidth: '800px' }}>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('advertiser_id')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      ID {sortColumn === 'advertiser_id' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Name {sortColumn === 'name' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('company_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Company {sortColumn === 'company_name' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('district')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      District {sortColumn === 'district' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('state')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      State {sortColumn === 'state' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('end_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      End Date {sortColumn === 'end_date' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Amount {sortColumn === 'amount' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Email {sortColumn === 'email' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('contact_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Contact {sortColumn === 'contact_no' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('whatsapp_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      WhatsApp {sortColumn === 'whatsapp_no' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Status {sortColumn === 'status' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedAdvertisers().map((adv) => (
                    <tr key={adv.id} data-testid={`report-row-${adv.advertiser_id}`}>
                      <td>{adv.advertiser_id}</td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '150px' }}>{adv.name}</td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '150px' }}>{adv.company_name}</td>
                      <td>{adv.district}</td>
                      <td>{adv.state}</td>
                      <td>{(adv.end_month && adv.end_year) ? `${adv.end_month} ${adv.end_year}` : (adv.end_date || '-')}</td>
                      <td>{adv.amount}</td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '180px' }}>{adv.email}</td>
                      <td>{adv.contact_no}</td>
                      <td>{adv.whatsapp_no || '-'}</td>
                      <td>{adv.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="form-section">
            <p className="text-gray-600 text-center">Use search filters above to find advertisers and generate reports.</p>
          </div>
        )}
      </div>
      
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          
          .no-print {
            display: none !important;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          table {
            font-size: 9px !important;
            width: 100% !important;
          }
          
          th, td {
            padding: 3px !important;
            font-size: 9px !important;
          }
          
          * {
            color: black !important;
          }
          
          h1, h2 {
            color: black !important;
          }
          
          th {
            background-color: white !important;
            color: black !important;
            border: 1px solid black !important;
          }
          
          td {
            border: 1px solid #ccc !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdvertiserReports;